import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import { ExtendLocales } from 'src/enums'
import Trns from 'src/localization/Trns'
import { privacyUrl, termsUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectExternalPrivacyUrl,
  selectExternalTermsUrl,
  selectFeedbackSurveyUrlFromState,
  selectPrimaryDomain,
} from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

interface ProfileQuicklinksBoxProps {
  className?: string
}

function prettifyURL(link: string): string {
  return link.replace(/(^\w+:|^)\/\/www.|(^\w+:|^)\/\//, '')
}

const TitleSpan = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )}
`

const ProfileQuicklinksBox: React.FC<
  ProfileQuicklinksBoxProps
> = (): React.ReactElement => {
  const externalTermsUrl = useSelector(selectExternalTermsUrl)
  const externalPrivacyUrl = useSelector(selectExternalPrivacyUrl)
  const primaryDomain = useSelector(selectPrimaryDomain)
  const feedbackSurveyUrl = useSelector(selectFeedbackSurveyUrlFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()

  const links = [
    {
      label: <Trns source={'Terms of Service'} />,
      url: externalTermsUrl || termsUrl(),
      prettyUrl: prettifyURL(
        externalTermsUrl || `${primaryDomain}${termsUrl()}`,
      ),
    },
    {
      label: <Trns source={'Privacy Policy'} />,
      url: externalPrivacyUrl || privacyUrl(),
      prettyUrl: prettifyURL(
        externalPrivacyUrl || `${primaryDomain}${privacyUrl()}`,
      ),
    },
  ]

  const handleTrackingViewLink = (url: string): string => {
    return matchPath(url, externalTermsUrl || termsUrl())
      ? 'profile settings - view terms of service - text link press'
      : matchPath(url, externalPrivacyUrl || privacyUrl())
        ? 'profile settings - view privacy - text link press'
        : ''
  }

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        background-color: transparent;
        border-left: 1px solid ${styleConstants.colors.gray_medium};
      `}
      noShadow
    >
      {locale === ExtendLocales.enUS && (
        <>
          <TitleSpan>
            <Trns source={'Have Feedback?'} />
          </TitleSpan>
          <span
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
              margin-top: 20px;
            `}
          >
            <Trns
              source={
                "We'd love to hear from you! We're always looking for new ideas and ways to improve your experience in ${appName}."
              }
              expressions={{
                appName: <AppName />,
              }}
            />
          </span>
          <span
            onClick={(): void => {
              analytics?.track(
                'profile settings - view feedback survey - text link press',
                {
                  screen: '/profile/overview',
                  interaction: 'text link press',
                  intent: 'view feedback survey',
                },
              )
            }}
            onKeyPress={(): null => {
              return null
            }}
            role={'button'}
            tabIndex={0}
          >
            <HoverableLink
              href={feedbackSurveyUrl}
              css={css`
                margin-top: 20px;
                margin-bottom: 30px;
                display: flex;
                align-items: center;
              `}
              ariaLabelSource={'Feedback Survey'}
            >
              <Trns source={'Feedback Survey'} />
              <SvgArrowForward
                css={css`
                  transform: scale(calc(18 / 24));
                `}
              />
            </HoverableLink>
          </span>
        </>
      )}
      <TitleSpan>
        <Trns source={'Quicklinks'} />
      </TitleSpan>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 30px;
        `}
      >
        {links.map(
          (lnk, i) =>
            lnk !== undefined && (
              <div
                key={i}
                css={css`
                  display: flex;
                  flex-direction: column;
                  &:nth-child(n + 2) {
                    margin-top: 20px;
                  }
                `}
              >
                <FieldLabel>{lnk.label}</FieldLabel>
                <span
                  onClick={(): void => {
                    analytics?.track(handleTrackingViewLink(lnk.url), {
                      screen: '/profile/overview',
                      interaction: 'text link press',
                      intent: 'view ' + lnk.label.props.source,
                    })
                  }}
                  onKeyPress={(): null => {
                    return null
                  }}
                  role={'button'}
                  tabIndex={0}
                >
                  <HoverableLink
                    href={lnk.url}
                    ariaLabel={lnk.prettyUrl}
                    underline
                  >
                    {lnk.prettyUrl}
                  </HoverableLink>
                </span>
              </div>
            ),
        )}
      </div>
    </Box>
  )
}

export default ProfileQuicklinksBox
